import { Link } from "react-router-dom";
import { CheckCircle2, LogIn, LayoutDashboard, MessageCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { WHOP_LOGIN_URL } from "@/lib/api";
import Logo from "@/components/Logo";

export default function CheckoutSuccess() {
  const { isMember, isVip, loginEnabled, checking } = useAuth();
  const verified = !checking && isMember && isVip;

  return (
    <div className="mx-auto flex max-w-7xl items-center justify-center px-4 py-16 sm:px-6 lg:px-8" data-testid="checkout-success-page">
      <div className="panel w-full max-w-lg p-8 text-center">
        <div className="flex justify-center"><Logo compact /></div>
        <div className="mx-auto mt-6 flex h-16 w-16 items-center justify-center rounded-full border border-primary/40 bg-primary/10">
          <CheckCircle2 size={32} className="text-primary" data-testid="checkout-success-icon" />
        </div>
        {verified ? (
          <>
            <h1 className="mt-6 font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl" data-testid="checkout-success-title">
              Welcome to Money Printer VIP!
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-slate-400" data-testid="checkout-success-copy">
              Your membership is active. You&apos;re officially part of the Money Printer VIP community! Your premium picks, detailed analysis and private Discord access are ready.
            </p>
          </>
        ) : (
          <>
            <h1 className="mt-6 font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-4xl" data-testid="checkout-success-title">
              You&apos;re Almost VIP!
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-slate-400" data-testid="checkout-success-copy">
              Your checkout journey is nearly complete. Sign in with the same Whop account you used at checkout to verify your membership and unlock your VIP dashboard, premium picks and Discord access.
            </p>
          </>
        )}

        <div className="mt-8 space-y-3">
          {verified ? (
            <Link to="/dashboard" className="btn-primary w-full" data-testid="checkout-success-dashboard-btn">
              <LayoutDashboard size={16} /> Go to VIP Dashboard
            </Link>
          ) : loginEnabled ? (
            <a
              href={WHOP_LOGIN_URL}
              target="_top"
              className="btn-primary w-full"
              data-testid="checkout-success-login-btn"
              onClick={(e) => {
                if (window.self !== window.top) {
                  e.preventDefault();
                  window.open(WHOP_LOGIN_URL, "_blank", "noopener");
                }
              }}
            >
              <LogIn size={16} /> Verify My VIP Membership
            </a>
          ) : (
            <Link to="/login" className="btn-primary w-full" data-testid="checkout-success-login-btn">
              <LogIn size={16} /> Verify My VIP Membership
            </Link>
          )}
          {!verified && (
            <p className="text-xs text-slate-500" data-testid="checkout-success-note">
              Membership access is activated once your subscription is verified.
            </p>
          )}
          <Link to="/picks" className="btn-outline w-full" data-testid="checkout-success-picks-link">
            View Today&apos;s Picks
          </Link>
        </div>

        <ul className="mt-8 space-y-2 text-left text-xs text-slate-500" data-testid="checkout-success-steps">
          <li className="flex gap-2"><CheckCircle2 size={14} className="mt-0.5 shrink-0 text-primary" /> Membership is verified server-side the moment you sign in.</li>
          <li className="flex gap-2"><MessageCircle size={14} className="mt-0.5 shrink-0 text-primary" /> Your Discord invite and VIP role appear on the dashboard.</li>
          <li className="flex gap-2"><CheckCircle2 size={14} className="mt-0.5 shrink-0 text-primary" /> Manage or cancel your plan anytime from your Whop account.</li>
        </ul>
      </div>
    </div>
  );
}
